// tslint:disable-next-line deprecation
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { RouterEventDetail, alertController, modalController } from '@ionic/core';
import {
  DeepLinkOpen,
  FirebaseDynamicLinks,
} from '@pantrist/capacitor-firebase-dynamic-links';
import {
  Component,
  ComponentInterface,
  State,
  Watch,
  h
} from '@stencil/core';
import { store } from '@stencil/redux';

import { AppService } from '../../services/app.service';
import PollingService from '../../services/polling.service';
import { setupSentry } from '../../services/sentry.service';
import StencilService from '../../services/stencil.service';
import {
  appDismissTutorial,
  appUpdateNetwork,
  installProject,
  loadApp
} from '../../store/app/app.actions';
import { initialStore } from '../../store/index';
import { ApplicationState } from '../../store/reducers';
import {
  getAppId, getAppReady,
  getAppsCount,
  getAuthIsAuth,
  getAuthSignupProjectId,
  getAuthUserImageUrl,
  getShouldShowTutorial
} from '../../store/selectors';
import { CoreoApiAppSummary, CoreoAppSummary, Unthunk } from '../../types';
import { authSetSignupProjectId, verifySSOLogin } from '../../store/auth/auth.actions';
import { formsSupportSetRestoreMediaUrl } from '../../store/formsSupport/formsSupport.actions';
import { Browser } from '@capacitor/browser';

interface AppDialogModalDetails {
  modal: HTMLIonModalElement;
  projectId: number;
}

@Component({
  tag: 'app-root',
  styleUrl: 'app-root.scss'
})
export class AppRoot implements ComponentInterface {

  @State() isAuth = false;
  @State() ready = false;
  @State() appsCount: number;
  @State() userImageUrl: string;
  @State() appId: number;
  @State() signupProjectId: number;

  appDismissTutorial!: typeof appDismissTutorial;
  loadApp!: Unthunk<typeof loadApp>;
  appUpdateNetwork!: Unthunk<typeof appUpdateNetwork>;
  authSetSignupProjectId!: Unthunk<typeof authSetSignupProjectId>;
  installProject!: Unthunk<typeof installProject>;
  formsSupportSetRestoreMediaUrl!: Unthunk<typeof formsSupportSetRestoreMediaUrl>;
  verifySSOLogin!: Unthunk<typeof verifySSOLogin>;

  private currentAppDialogModalDetails?: AppDialogModalDetails;

  @Watch('ready') async handleReady() {
    if (this.ready) {
      await this.tutorialTest();
      await AppService.instance.offlineMigrationTest();
      SplashScreen.hide();
      PollingService.instance.init();
      StencilService.instance.init();
    }
  }

  @Watch('isAuth') handleAuth() {
    this.tutorialTest();
  }

  @Watch('appId') handleSwitchApp() {
    // If there is an update toast displayed, get rid of it
    const toast: HTMLIonToastElement = document.getElementById('app-update-toast') as HTMLIonToastElement;
    if (toast) {
      toast.dismiss();
    }
  }

  private async tutorialTest() {
    if (!this.ready) {
      return;
    }

    if (!this.postAuthInstallTest()) {
      const state = store.getState();
      if (getShouldShowTutorial(state)) {
        return this.presentTutorialModal();
      }
    }
  }

  private async postAuthInstallTest() {
    if (!(this.isAuth && this.signupProjectId)) {
      return false;
    }
    this.installAndNavigateApp(this.signupProjectId);
    this.authSetSignupProjectId(null);
    return true;
  }

  async loadAndNavigateApp(id: number) {
    await this.loadApp(id);
    await document.querySelector('ion-nav').popToRoot();
  }

  async presentAppInstallDialog(id: number) {
    if (this.currentAppDialogModalDetails) {
      if (this.currentAppDialogModalDetails.projectId === id) {
        return;
      }
      await this.currentAppDialogModalDetails.modal.dismiss();
    }

    let app: CoreoApiAppSummary;;
    let apps: CoreoAppSummary[];

    try {
      app = await AppService.instance.getAppSummaryFromAPI(id);
      apps = await AppService.instance.getAppsList();
    } catch (error) {
      console.error(error);

      const alert = await alertController.create({
        header: 'There was a problem!',
        message: error.message,
        buttons: [{
          text: 'Ok'
        }]
      })

      return alert.present();
    }

    // If the user doesn't have access to this app, join the project for later access
    const appAvailable = apps.find(a => a.id === +id) !== undefined;
    if (!appAvailable && app.allowContributors) {
      try {
        await AppService.instance.joinProject(+id);
      } catch (error) {
        const alert = await alertController.create({
          header: 'Problem joining the project',
          message: "The project has no remaining seats. Please contact the project administrator.",
          buttons: [{
            text: 'Ok'
          }]
        });
        return alert.present();
      }
    }

    const modal = await modalController.create({
      component: 'app-summary',
      componentProps: {
        app,
        id: 'appSummary'
      }
    });

    modal.present();

    this.currentAppDialogModalDetails = {
      modal,
      projectId: id
    }

    const { role } = await modal.onWillDismiss();
    console.log('AppRoot.presentAppInstallDialog() -> onWillDismiss, role: ', role);

    this.currentAppDialogModalDetails = null;

    if (role === 'load') {
      this.loadAndNavigateApp(id);
    }
  }

  async installAndNavigateApp(id: number) {
    if (this.appId === id) {
      console.log('Already on this app');
      return;
    }
    await AppService.instance.joinProject(id);
    await this.installProject(id);
    await this.loadAndNavigateApp(id);
  }

  async handleSSOLogin(url: URL) {
    const error = url.searchParams.get('error');
    const errorDescription = url.searchParams.get('error_description');

    if (error && errorDescription) {
      const alert = await alertController.create({
        header: 'There was a problem!',
        message: errorDescription,
        buttons: [{
          text: 'Ok'
        }]
      })
      return alert.present();
    }

    const token = url.searchParams.get('auth_token');
    if (token) {
      return this.verifySSOLogin(token);
    }
  }

  handleDeepLink = (data: DeepLinkOpen) => {
    const url = new URL(data.url);

    const { pathname } = url;
    const [, , id] = pathname.split('/');

    if (!id) {
      return;
    }

    if (this.isAuth) {
      // If we're already on this app, do nothing
      if (this.appId !== +id) {
        this.presentAppInstallDialog(+id);
      }
    } else {
      // Set the signup project id so it will be handled after authentication
      this.authSetSignupProjectId(+id);
    }
  }

  async componentWillLoad() {
    store.setStore(initialStore);
    setupSentry();

    store.mapStateToProps(this, (state: ApplicationState) => ({
      isAuth: getAuthIsAuth(state),
      ready: getAppReady(state),
      appsCount: getAppsCount(state),
      userImageUrl: getAuthUserImageUrl(state),
      appId: getAppId(state),
      signupProjectId: getAuthSignupProjectId(state)
    }));

    store.mapDispatchToProps(this, {
      appDismissTutorial,
      loadApp,
      appUpdateNetwork,
      authSetSignupProjectId,
      installProject,
      formsSupportSetRestoreMediaUrl,
      verifySSOLogin
    });

    if (Capacitor.isNativePlatform()) {
      FirebaseDynamicLinks.addListener('deepLinkOpen', this.handleDeepLink);
    }

    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Light });

      if (Capacitor.getPlatform() === 'android') {
        StatusBar.setBackgroundColor({
          color: '#0069df'
        });
      }

      Keyboard.addListener('keyboardWillShow', () => {
        document.querySelector('ion-app').classList.add('keyboard-open');
      });
      Keyboard.addListener('keyboardDidHide', () => {
        document.querySelector('ion-app').classList.remove('keyboard-open');
      });
    }

    Network.addListener('networkStatusChange', status => this.appUpdateNetwork(status.connected, status.connectionType));
    Network.getStatus().then(status => this.appUpdateNetwork(status.connected, status.connectionType));

    App.addListener('appRestoredResult', async ({ pluginId, data }) => {
      if (pluginId === 'Camera') {
        if (data) {
          this.formsSupportSetRestoreMediaUrl(data.path);
        }
      }
    });

    App.addListener('appUrlOpen', async (data: URLOpenListenerEvent) => {
      const url = new URL(data.url);
      await this.handleSSOLogin(url);
      Browser.close();
    });

    if (!Capacitor.isNativePlatform()) {
      this.handleSSOLogin(new URL(window.location.href));
    }
  }

  async presentTutorialModal() {
    const modal = await modalController.create({
      component: 'coreo-tutorial',
    });

    modal.onDidDismiss().then(() => {
      this.appDismissTutorial();
      this.postAuthInstallTest();
    });

    await modal.present();
  }

  handleRouteChange(e: CustomEvent<RouterEventDetail>) {
    if (Capacitor.isNativePlatform()) {
      FirebaseAnalytics.setCurrentScreen({
        screenName: e.detail.to,
        screenClassOverride: e.detail.to
      });
      FirebaseCrashlytics.setCustomKey({
        key: 'page',
        value: e.detail.to,
        type: 'string'
      });
    }
  }

  renderRouter() {
    const redirects: HTMLIonRouteRedirectElement[] = [];
    if (!this.isAuth) {
      redirects.push(<ion-route-redirect from="*" to="/login" />);
    } else {
      redirects.push(<ion-route-redirect from="/login" to="/" />);
      if (this.appsCount === 0) {
        redirects.push(<ion-route-redirect from="/pages/*" to="/" />);
        redirects.push(<ion-route-redirect from="/records/*" to="/" />);
        redirects.push(<ion-route-redirect from="/no-content/*" to="/" />);
        redirects.push(<ion-route-redirect from="/pending" to="/" />);
      }
    }

    return <ion-router useHash={false} onIonRouteDidChange={e => this.handleRouteChange(e)}>
      {redirects}
      <ion-route url="/" component="app-home" />
      <ion-route url="/login" component="app-auth-root" />
      {/* <ion-route url="/records/:recordId" component="app-record" /> */}
      <ion-route url="/pending" component="app-pending-records" />
      <ion-route url="/pages/:pageId" component="app-page" />
      <ion-route url="/pages/:pageId/:param" component="app-page" />

      <ion-route url="/account" component="account-page" />
      <ion-route url="/account/edit-profile" component="edit-profile-page" />
      <ion-route url="/projects" component="app-projects"></ion-route>
      <ion-route url="/help" component="app-help"></ion-route>
      <ion-route url="/help/info" component="app-help-info"></ion-route>
      <ion-route url="/maps" component="app-maps"></ion-route>
      <ion-route url="/settings" component="app-settings"></ion-route>
    </ion-router>;
  }

  renderMenu() {
    if (!this.isAuth) {
      return;
    }

    return (
      <ion-menu contentId="main" disabled={!this.isAuth}>
        <ion-content forceOverscroll={false}>
          <app-menu />
        </ion-content>
      </ion-menu>
    );
  }

  render() {
    if (!this.ready) {
      return;
    }

    return (
      <ion-app>
        <ion-split-pane contentId="main" disabled={!this.isAuth}>
          {this.renderMenu()}
          {this.renderRouter()}
          <ion-nav id="main" swipeGesture={false} />
        </ion-split-pane>
        <app-sync-modal />
      </ion-app>
    );
  }
}
